import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@insio/ui-toolkit';
import { formatDotTimestamp } from '@insio/dates';
import { CancelAccountDeletionDocument } from '@/graphql';
import { useMasterAccountBoardErrorMessage } from '@/hooks/use-master-account-board-error';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useAuth } from '../context';
import { useSignOut } from '../hooks';
import { useSwitchAccount } from '../hooks/use-switch-account';

/** No-op handler for the non-dismissable modal */
const noop = () => {};

/** CSS class to hide the close button rendered by Modal.Header */
const HIDE_CLOSE_BTN = '[&_[role=button]]:hidden!';

/**
 * Full-screen, non-dismissable gate shown when the current account is PENDING_DELETION.
 * The ways out are reactivating the account, leaving for another organization, or logging out.
 */
export function AccountPendingDeletionGate() {
  const { t } = useTranslation();
  const { user, refetch } = useAuth();
  const { signOut, loading: signingOut } = useSignOut();
  const [reactivate, { loading: reactivating }] = useMutation(CancelAccountDeletionDocument);
  const { targets, activeTargetId, switchTo, switching } = useSwitchAccount();
  const { showError } = useToastHelpers();
  const masterAccountBoardError = useMasterAccountBoardErrorMessage();

  const scheduledAt = user?.account?.deletionScheduledAt;
  const scheduledLabel = scheduledAt ? formatDotTimestamp(scheduledAt) : '';

  // One organization being scheduled for deletion must not strand a member in it — without
  // this the gate offers only reactivation, which a non-admin cannot perform. A master account the person
  // belongs to is a way out too.
  const otherAccounts = targets.filter(target => target.id !== activeTargetId);
  const busy = reactivating || signingOut || switching;

  const handleReactivate = async () => {
    try {
      const { error } = await reactivate();
      // errorPolicy 'all' resolves a GraphQL refusal instead of throwing it.
      if (error) throw error;
    } catch (e) {
      showError(
        masterAccountBoardError(e) ?? t('features.auth.accountPendingDeletion.reactivateError')
      );
      return;
    }
    await refetch();
  };

  return (
    <Modal open onClose={noop} size='sm' layout='vertical'>
      <Modal.Header onClose={noop} className={HIDE_CLOSE_BTN}>
        <span className='text-lg font-medium'>
          {t('features.auth.accountPendingDeletion.title')}
        </span>
      </Modal.Header>

      <Modal.Content className='flex flex-col gap-lg p-lg sm:p-xl'>
        <p className='text-sm text-foreground-muted'>
          {t('features.auth.accountPendingDeletion.description', { date: scheduledLabel })}
        </p>

        <div className='flex flex-col gap-sm'>
          <Button
            variant='primary'
            size='full'
            disabled={busy}
            onClick={() => void handleReactivate()}
          >
            {t('features.auth.accountPendingDeletion.reactivate')}
          </Button>

          {otherAccounts.length > 0 && (
            <div className='flex flex-col gap-xs'>
              <span className='text-sm text-foreground-muted'>
                {t('features.auth.accountPendingDeletion.switchHint')}
              </span>
              {otherAccounts.map(account => (
                <Button
                  key={account.id}
                  variant='secondary'
                  size='full'
                  disabled={busy}
                  onClick={() => void switchTo(account.id)}
                >
                  {t('features.auth.accountPendingDeletion.switchTo', { name: account.name })}
                </Button>
              ))}
            </div>
          )}

          <Button variant='ghost' size='full' disabled={busy} onClick={signOut}>
            {t('features.auth.accountPendingDeletion.logout')}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
}
